// FLOW-01 (PLAN P11): o Monaco do editor de flow, montado à mão e carregado só na rota do editor.
//
// Não é o `monaco-editor` inteiro: o `editor.main` traz todas as linguagens e os serviços de CSS,
// HTML, JSON e TypeScript, e o editor de flow só escreve YAML. Entram a API, o YAML do Monarch (a
// cor) e as contribuições que quem edita código usa: sugestão, hover, busca, dobra, comentário,
// erro na linha e os comandos de cursor. O resto do que o `editor.main` registra não faz falta aqui.
//
// O schema chega pelo monaco-yaml, que dá o autocomplete e o hover. A VALIDAÇÃO dele fica
// desligada de propósito: a autoridade é o compilador do PowerTools (PLAN §2), que já roda o mesmo
// schema na passada 2 e devolve a mensagem em português com o caminho do passo. Ligada, cada campo
// errado apareceria duas vezes na linha, uma delas em inglês e com outro texto.
import * as monaco from 'monaco-editor/editor/editor.api.js';
import 'monaco-editor/languages/definitions/yaml/register.js';
// FLOW-05 (task 5.1): o modelo do PDF (`document.render`) é HTML com as trocas `{{ }}`, que é o que
// a linguagem handlebars do Monaco pinta (o HTML e as marcas). Só o registro mora aqui: o
// tokenizador vem num chunk à parte, na primeira caixa de `document.render` que abre.
import 'monaco-editor/languages/definitions/handlebars/register.js';
import 'monaco-editor/features/codeEditor/register.js';
import 'monaco-editor/features/codicon/register.js';
import 'monaco-editor/features/bracketMatching/register.js';
import 'monaco-editor/features/clipboard/register.js';
import 'monaco-editor/features/comment/register.js';
import 'monaco-editor/features/contextmenu/register.js';
import 'monaco-editor/features/cursorUndo/register.js';
import 'monaco-editor/features/find/register.js';
import 'monaco-editor/features/folding/register.js';
import 'monaco-editor/features/gotoError/register.js';
import 'monaco-editor/features/hover/register.js';
import 'monaco-editor/features/indentation/register.js';
import 'monaco-editor/features/linesOperations/register.js';
import 'monaco-editor/features/multicursor/register.js';
import 'monaco-editor/features/snippet/register.js';
import 'monaco-editor/features/suggest/register.js';
// O Ctrl+M (⌃⇧M no Mac) solta o Tab, que no código indenta, para o teclado sair do editor; a faixa
// do código o anuncia (revisão de UX, M6).
import 'monaco-editor/features/toggleTabFocusMode/register.js';
import 'monaco-editor/features/wordHighlighter/register.js';
import 'monaco-editor/features/wordOperations/register.js';
import 'monaco-editor/editor/browser/coreCommands.js';
import 'monaco-editor/editor/contrib/suggest/browser/suggestController.js';
import 'monaco-editor/editor/contrib/gotoError/browser/markerSelectionStatus.js';
import 'monaco-editor/editor/common/standaloneStrings.js';
import { configureMonacoYaml } from 'monaco-yaml';
import EditorWorker from 'monaco-editor/editor/editor.worker.js?worker';
import YamlWorker from './yaml.worker.js?worker';
// FLOW-07: o serviço de JS do Monaco (o TypeScript em worker) só nasce quando a caixa de um passo
// `code` abre (`carregarJavaScript`). O `?worker` aqui é só o construtor: o arquivo do worker, de
// alguns MB, é baixado no primeiro `new`.
import TsWorker from 'monaco-editor/languages/features/typescript/ts.worker.js?worker';
// FLOW-12 (G7, G14): o JSON do teste (o objeto sem `properties` na gaveta, o "Editar como JSON" e a
// saída fixada) tem o serviço de JSON do Monaco, que marca na linha o JSON quebrado. Como o de TS, o
// worker só baixa no primeiro editor de JSON que abre.
import JsonWorker from 'monaco-editor/languages/features/json/json.worker.js?worker';

window.MonacoEnvironment = {
  getWorker(_id, label) {
    if (label === 'yaml') return new YamlWorker();
    if (label === 'json') return new JsonWorker();
    if (label === 'typescript' || label === 'javascript') return new TsWorker();
    return new EditorWorker();
  },
};

// O monaco-worker-manager 2.0.1 (do monaco-yaml) chama `monaco.editor.createWebWorker` com
// `{ label, createData }`, a assinatura de antes do 0.57. No 0.57 essa função quer o `worker` pronto,
// e com a chamada antiga o worker do YAML não nasce: o Monaco cai para a thread principal sem os
// métodos do YAML, e não há sugestão nem hover (medido no Histoire em 06/10). Aqui vai a ponte, no
// protocolo do `createWebWorker` interno do Monaco (o que o JSON dele usa): o worker recebe um
// 'ignore' e depois o `createData`. O interno não é importado porque arrasta todas as contribuições
// do editor (cerca de 140 KB gzip a mais no chunk, medido no build).
const criarWorkerLegado = ({ label, createData }) =>
  monaco.editor.createWebWorker({
    worker: Promise.resolve(
      window.MonacoEnvironment.getWorker('workerMain.js', label)
    ).then(worker => {
      worker.postMessage('ignore');
      worker.postMessage(createData);
      return worker;
    }),
  });

const monacoDoYaml = {
  ...monaco,
  editor: { ...monaco.editor, createWebWorker: criarWorkerLegado },
};

// Uma instância do monaco-yaml por página (é a regra dele): a segunda tela que abre só troca o schema.
let monacoYaml = null;

export function configurarSchema(schema) {
  const opcoes = {
    enableSchemaRequest: false,
    validate: false,
    format: { enable: false },
    schemas: [
      {
        uri:
          schema.$id ||
          'https://powerbizz.ai/schemas/powerflow/flow.schema.json',
        fileMatch: ['**/*.flow.yaml'],
        schema,
      },
    ],
  };
  if (monacoYaml) monacoYaml.update(opcoes);
  else monacoYaml = configureMonacoYaml(monacoDoYaml, opcoes);
}

// O tema sai dos tokens do OS (`--slate-*`, `--iris-*`, `--teal-*`, `--ruby-*`), lidos do
// elemento do editor: o mesmo `rgb(var(--x))` que o Tailwind usa nas classes `n-*`. O Monaco só
// aceita hex no tema, por isso a conversão. O fundo é transparente: quem pinta é o contêiner, com a
// classe do OS, e o editor nunca destoa da tela em volta.
const hexDoToken = (estilo, nome) => {
  const partes = estilo.getPropertyValue(nome).trim().split(/\s+/);
  if (partes.length < 3) return '#808080';
  return `#${partes
    .slice(0, 3)
    .map(p => Number(p).toString(16).padStart(2, '0'))
    .join('')}`;
};

// O magenta de leitura é um hex pronto (`--pz-brand-on-tint`, claro e escuro), e não a trinca dos
// tokens `n-*`.
const hexPronto = (estilo, nome, reserva) => {
  const valor = estilo.getPropertyValue(nome).trim();
  return /^#[0-9a-f]{6}$/i.test(valor) ? valor : reserva;
};

export function aplicarTema(elemento, escuro) {
  const estilo = getComputedStyle(elemento);
  const cor = nome => hexDoToken(estilo, nome);
  const texto = cor('--slate-12');
  // FLOW-05: o modelo do PDF, como no mockup aprovado: a tag em iris, o valor do atributo em
  // teal e a troca `{{ }}` no magenta de leitura, o mesmo da fórmula no formulário.
  const troca = hexPronto(estilo, '--pz-brand-on-tint', cor('--iris-11'));
  monaco.editor.defineTheme('powerflow', {
    base: escuro ? 'vs-dark' : 'vs',
    inherit: true,
    rules: [
      { token: '', foreground: texto.slice(1) },
      { token: 'type', foreground: cor('--iris-11').slice(1) },
      { token: 'string', foreground: texto.slice(1) },
      { token: 'number', foreground: cor('--teal-11').slice(1) },
      { token: 'keyword', foreground: cor('--teal-11').slice(1) },
      {
        token: 'comment',
        foreground: cor('--slate-10').slice(1),
        fontStyle: 'italic',
      },
      { token: 'operators', foreground: cor('--slate-10').slice(1) },
      { token: 'tag.html', foreground: cor('--iris-11').slice(1) },
      { token: 'delimiter.html', foreground: cor('--iris-11').slice(1) },
      { token: 'attribute.name', foreground: cor('--slate-11').slice(1) },
      { token: 'attribute.value', foreground: cor('--teal-11').slice(1) },
      { token: 'delimiter.handlebars', foreground: troca.slice(1) },
      { token: 'variable.parameter.handlebars', foreground: troca.slice(1) },
      { token: 'keyword.helper.handlebars', foreground: troca.slice(1) },
      // FLOW-12: o JSON do teste, como no mockup aprovado: a chave na tinta, o texto na tinta
      // secundária, e o número e o verdadeiro/falso em iris.
      { token: 'string.key.json', foreground: texto.slice(1) },
      { token: 'string.value.json', foreground: cor('--slate-11').slice(1) },
      { token: 'number.json', foreground: cor('--iris-11').slice(1) },
      { token: 'keyword.json', foreground: cor('--iris-11').slice(1) },
    ],
    colors: {
      'editor.background': '#00000000',
      'editor.foreground': texto,
      'editorLineNumber.foreground': cor('--slate-8'),
      'editorLineNumber.activeForeground': cor('--slate-11'),
      'editor.lineHighlightBackground': `${cor('--slate-3')}80`,
      'editor.lineHighlightBorder': '#00000000',
      'editorGutter.background': '#00000000',
      'editorError.foreground': cor('--ruby-10'),
      'editorWarning.foreground': cor('--amber-10'),
      'editorWidget.background': cor('--slate-2'),
      'editorWidget.border': cor('--slate-6'),
      'editorSuggestWidget.background': cor('--slate-2'),
      'editorSuggestWidget.border': cor('--slate-6'),
      'editorSuggestWidget.selectedBackground': cor('--slate-4'),
      'editorSuggestWidget.selectedForeground': texto,
      'editorSuggestWidget.foreground': texto,
      'editorSuggestWidget.highlightForeground': cor('--iris-11'),
      'list.activeSelectionBackground': cor('--slate-4'),
      'list.activeSelectionForeground': texto,
      'list.focusBackground': cor('--slate-4'),
      'list.focusForeground': texto,
      'list.hoverBackground': cor('--slate-3'),
      'editorHoverWidget.background': cor('--slate-2'),
      'editorHoverWidget.border': cor('--slate-6'),
    },
  });
  monaco.editor.setTheme('powerflow');
}

// FLOW-07 (task 4.2): a linguagem JavaScript e o serviço de TypeScript que a atende, carregados na
// primeira caixa de passo `code` (o resto do editor de flow não paga por eles). O escopo é o do
// coderunner: a biblioteca do ES2020, sem DOM (não há `window` nem `fetch` lá), e o `return` solto,
// que no coderunner é o corpo de uma função, não é erro (TS1108). As declarações do escopo e das
// bibliotecas entram por `addExtraLib`, pela caixa aberta.
let javascript = null;
export function carregarJavaScript() {
  if (!javascript) {
    javascript =
      import('monaco-editor/languages/definitions/javascript/register.js')
        .then(
          () =>
            import('monaco-editor/languages/features/typescript/register.js')
        )
        .then(ts => {
          ts.javascriptDefaults.setCompilerOptions({
            target: ts.ScriptTarget.ES2020,
            lib: ['es2020'],
            allowJs: true,
            checkJs: true,
            allowNonTsExtensions: true,
            noEmit: true,
          });
          ts.javascriptDefaults.setDiagnosticsOptions({
            noSemanticValidation: false,
            noSyntaxValidation: false,
            diagnosticCodesToIgnore: [1108],
          });
          return ts.javascriptDefaults;
        });
  }
  return javascript;
}

// FLOW-12 (G7, G14): a linguagem JSON, na primeira vez que um `CodigoJson` abre. Só a conferência do
// JSON e a dobra: sem schema (nenhum pedido de rede) e sem as sugestões, que no dado do teste não
// têm o que sugerir. Comentário e vírgula no fim são erro, como no `JSON.parse` que salva.
let json = null;
export function carregarJson() {
  if (!json) {
    json = import('monaco-editor/languages/features/json/register.js').then(
      ({ jsonDefaults }) => {
        jsonDefaults.setDiagnosticsOptions({
          validate: true,
          allowComments: false,
          comments: 'error',
          trailingCommas: 'error',
          schemas: [],
          enableSchemaRequest: false,
        });
        jsonDefaults.setModeConfiguration({
          documentFormattingEdits: true,
          documentRangeFormattingEdits: true,
          completionItems: false,
          hovers: false,
          documentSymbols: false,
          tokens: true,
          colors: false,
          foldingRanges: true,
          diagnostics: true,
          selectionRanges: true,
        });
        return jsonDefaults;
      }
    );
  }
  return json;
}

export { monaco };
